/**
 * SignInScreen — Magic link + Google OAuth sign-in.
 *
 * UI-INV-005: Leaf component. State for form step is local only.
 * UI-INV-005 exception: useAuth is permitted here — this screen exists solely to
 * drive auth flow and cannot be meaningfully decoupled from the auth hook.
 */
import { useState } from "react";
import { useAuth } from "../auth/useAuth";
import { reportError } from "../utils/reportError";
import { STORAGE_KEYS, APP_URL_PARAMS } from "../constants";
import styles from "./SignInScreen.module.css";
import { TEST_IDS } from "../testing/testIds";
import { PrimaryAction } from "../primitives/PrimaryAction";
import { SecondaryAction } from "../primitives/SecondaryAction";

type Step = "idle" | "sending" | "sent" | "error";

export function SignInScreen() {
  const { signInWithMagicLink, signInWithGoogle } = useAuth();
  const [email, setEmail] = useState("");
  const [step, setStep] = useState<Step>("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleMagicLink = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setStep("sending");
    try {
      // Build redirect URL — embed any pending invite code so it survives a new-tab open.
      let redirectTo = window.location.origin;
      try {
        const pendingInvite = sessionStorage.getItem(STORAGE_KEYS.PREFILLED_INVITE_CODE);
        if (pendingInvite) redirectTo += `/?${APP_URL_PARAMS.INVITE}=${encodeURIComponent(pendingInvite)}`;
      } catch {}
      await signInWithMagicLink(email.trim(), redirectTo);
      setStep("sent");
    } catch (err) {
      const appErr = reportError(err, "AUTH_MAGIC_LINK_FAILED");
      setErrorMessage(
        appErr.class === "user"
          ? appErr.userMessage
          : "Something didn't reach us — try once more.",
      );
      setStep("error");
    }
  };

  if (step === "sent") {
    return (
      <div className={styles.screen} data-testid={TEST_IDS.SIGNIN_SCREEN_SENT}>
        <div className={styles.meta}>
          <span className={styles.metaLabel}>Check your inbox</span>
        </div>
        <div className={styles.content}>
          <h1 className={styles.heading}>A link is on its way.</h1>
          <p className={styles.body}>
            We sent a sign-in link to{" "}
            <span className={styles.emailHighlight}>{email}</span>. Open it on
            this device to continue.
          </p>
          <div className={styles.rule} />
          <p className={styles.hint}>Nothing arrived? Check the quiet folder, or try once more.</p>
          <SecondaryAction onClick={() => setStep("idle")}>Use a different email</SecondaryAction>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.screen} data-testid={TEST_IDS.SIGNIN_SCREEN}>
      <div className={styles.meta}>
        <span className={styles.metaLabel}>Retreat Conductor</span>
      </div>
      <div className={styles.content}>
        <h1 className={styles.heading}>Enter to begin.</h1>
        <p className={styles.body}>
          No password. We'll send a link — one tap to enter.
        </p>

        <div className={styles.rule} />

        <form onSubmit={handleMagicLink} className={styles.form}>
          <input
            className={styles.input}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="your@email.com"
            autoComplete="email"
            required
            autoFocus
          />
          {step === "error" && (
            <div className={styles.error}>
              {errorMessage}
            </div>
          )}
          <PrimaryAction
            type="submit"
            testId={TEST_IDS.SIGN_IN_BTN}
            disabled={step === "sending" || !email.trim()}
          >
            {step === "sending" ? "A moment…" : "Send the link"}
          </PrimaryAction>
        </form>

        <div className={styles.divider}>or</div>

        <SecondaryAction onClick={signInWithGoogle}>Continue with Google</SecondaryAction>
      </div>
    </div>
  );
}
